import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { EmpresaSelect } from '../../shared/components/EmpresaSelect';
import { canonicalToBrl } from '../../shared/components/MoneyInput';
import { useCompanyDocumentosFiscaisHooks } from '../empresa/documentos-fiscais/hooks';
import { useEmitirNotaFiscalRc } from './hooks';

/** Modelo fixo do fluxo (RPS/NFS-e). Espelha `GerarNotaFiscalEmpresaPage`. */
const MODELO_NF_FIXO = '65';
const MODELO_NF_LABEL = 'RPS/NFS-e';

export interface EmitirNotaFiscalDialogProps {
  open: boolean;
  purchaseRequestId: number;
  /** Saldo atual (canônico) — valor da Nota-Mãe que será gerada. */
  saldo: string;
  onClose: () => void;
}

/**
 * Emitir NF (Nota-Mãe) da RC. Form de config: empresa emitente + modelo fixo
 * (RPS/NFS-e) + série. O valor é o saldo atual (server computa). Card [spjIqsFB].
 */
export function EmitirNotaFiscalDialog({
  open,
  purchaseRequestId,
  saldo,
  onClose,
}: EmitirNotaFiscalDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const emitir = useEmitirNotaFiscalRc(purchaseRequestId);

  const [empresaCodigo, setEmpresaCodigo] = useState<number | null>(null);
  const [serieOverride, setSerieOverride] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const docsHooks = useCompanyDocumentosFiscaisHooks(empresaCodigo ?? 0);
  const docsQuery = docsHooks.useList({
    page: 1,
    pageSize: 100,
    sortBy: 'serie',
    sortOrder: 'asc',
  });

  const seriesDisponiveis = useMemo(
    () =>
      (docsQuery.data?.data ?? [])
        .filter((d) => String(d.modelo) === MODELO_NF_FIXO)
        .map((d) => d.serie),
    [docsQuery.data],
  );

  // Auto-seleciona a série quando há só uma (espelha o WWP).
  const serie =
    serieOverride ?? (seriesDisponiveis.length === 1 ? (seriesDisponiveis[0] ?? '') : '');

  const serieInvalida = !serie;
  const empresaInvalida = empresaCodigo === null;
  const isPending = emitir.isPending;

  async function handleSubmit(): Promise<void> {
    setTouched(true);
    if (empresaInvalida || serieInvalida) return;
    try {
      await emitir.mutateAsync({
        empresaCodigo: empresaCodigo as number,
        modelo: MODELO_NF_FIXO,
        serie,
      });
      toast.success(t('features.requisicoesCompra.emitirNf.toastOk'));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onExited: () => {
          setEmpresaCodigo(null);
          setSerieOverride(null);
          setTouched(false);
        },
      }}
    >
      <DialogTitle>{t('features.requisicoesCompra.emitirNf.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            {t('features.requisicoesCompra.emitirNf.aviso', {
              valor: `R$ ${canonicalToBrl(saldo) || saldo}`,
            })}
          </Alert>

          <EmpresaSelect
            value={empresaCodigo}
            onChange={(id) => {
              setEmpresaCodigo(id);
              setSerieOverride(null); // troca de empresa reseta a série
            }}
            label={t('features.requisicoesCompra.emitirNf.empresaLabel')}
            error={touched && empresaInvalida}
            helperText={
              touched && empresaInvalida
                ? t('features.requisicoesCompra.emitirNf.empresaRequired')
                : undefined
            }
          />

          <TextField
            label={t('features.requisicoesCompra.emitirNf.modeloLabel')}
            value={`${MODELO_NF_FIXO} — ${MODELO_NF_LABEL}`}
            InputProps={{ readOnly: true }}
            fullWidth
          />

          <TextField
            select
            label={t('features.requisicoesCompra.emitirNf.serieLabel')}
            value={serie}
            onChange={(e) => setSerieOverride(e.target.value)}
            disabled={
              empresaCodigo === null || docsQuery.isFetching || seriesDisponiveis.length === 0
            }
            error={touched && serieInvalida && empresaCodigo !== null}
            helperText={
              empresaCodigo !== null && seriesDisponiveis.length === 0 && !docsQuery.isFetching
                ? t('features.requisicoesCompra.emitirNf.serieVazia')
                : touched && serieInvalida && empresaCodigo !== null
                  ? t('features.requisicoesCompra.emitirNf.serieRequired')
                  : undefined
            }
            fullWidth
          >
            <MenuItem value="">
              <em>{t('features.requisicoesCompra.emitirNf.seriePlaceholder')}</em>
            </MenuItem>
            {seriesDisponiveis.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('features.requisicoesCompra.emitirNf.valorLabel')}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              R$ {canonicalToBrl(saldo) || saldo}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={isPending}>
          {isPending ? t('common.saving') : t('features.requisicoesCompra.emitirNf.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
